import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 
  Users, 
  Target, 
  BookOpen, 
  GitBranch, 
  Play, 
  Pause,
  ChevronLeft,
  ChevronRight,
  Medal,
  CheckCircle2,
  AlertTriangle,
  PenTool,
  Sparkles,
  ShieldCheck
} from "lucide-react";
import recoverMeLogo from "@assets/forgerestore-logo_1779932627674.png";

interface Scene {
  id: number;
  title: string;
  description: string;
  icon: any;
  gradient: string;
  accentColor: string;
  bullets?: string[];
}

const scenes: Scene[] = [
  {
    id: 1,
    title: "Track Triggers & Cravings",
    description: "Log and monitor your triggers and cravings with our comprehensive tracking system",
    icon: AlertTriangle,
    gradient: "from-rose-500/30 via-orange-500/20 to-amber-500/30",
    accentColor: "rose",
  },
  {
    id: 2,
    title: "Daily Recovery Journaling",
    description: "Reflect on your journey with secure, private journal entries that support your recovery",
    icon: PenTool,
    gradient: "from-blue-500/30 via-cyan-500/20 to-teal-500/30",
    accentColor: "blue",
  },
  {
    id: 4,
    title: "Connect with Your Sponsor",
    description: "Build meaningful recovery relationships through our sponsor-sponsee pairing system",
    icon: Users,
    gradient: "from-emerald-500/30 via-green-500/20 to-teal-500/30",
    accentColor: "emerald",
  },
  {
    id: 5,
    title: "Integrated Stepwork Portal",
    description: "Sponsor and sponsee can collaborate on step work in one convenient location with literature, writing, and discussion tasks organized by step and category",
    icon: BookOpen,
    // Task #348 — inherited the violet scheme from the removed
    // "Pattern Reflections" slide (was amber/yellow/orange).
    gradient: "from-violet-500/30 via-purple-500/20 to-fuchsia-500/30",
    accentColor: "violet",
  },
  {
    id: 6,
    title: "Your Sponsorship Family",
    description: "See your sponsorship lineage and celebrate collective progress together",
    icon: GitBranch,
    gradient: "from-cyan-500/30 via-teal-500/20 to-emerald-500/30",
    accentColor: "cyan",
  },
  {
    id: 7,
    title: "Celebrate Your Milestones",
    description: "Mark sobriety achievements with color-coded milestone chips - from 30 days to 5 years",
    icon: Medal,
    gradient: "from-pink-500/30 via-rose-500/20 to-red-500/30",
    accentColor: "pink",
  },
  {
    id: 8,
    title: "Private by design",
    description: "Your recovery is yours. We never sell your information — ever.",
    icon: ShieldCheck,
    gradient: "from-indigo-500/30 via-blue-500/20 to-cyan-500/30",
    accentColor: "indigo",
    bullets: [
      "Anonymous 8-character ForgeRestoreID — you choose what name (if any) to display",
      "Only people you connect with can see your shared content",
      "Granular privacy controls: you decide what to share, per category",
      "Crisis alerts go only to your chosen support network",
      "Export or delete your data anytime — and we never sell it to third parties",
    ],
  },
];

export default function HeroCarousel() {
  const [currentScene, setCurrentScene] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (isPaused || prefersReducedMotion) return;

    const interval = setInterval(() => {
      setCurrentScene((prev) => (prev + 1) % scenes.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused, prefersReducedMotion]);

  const scene = scenes[currentScene];
  const Icon = scene.icon;

  const nextScene = () => {
    setCurrentScene((prev) => (prev + 1) % scenes.length);
  };

  const prevScene = () => {
    setCurrentScene((prev) => (prev - 1 + scenes.length) % scenes.length);
  };

  const animationVariants = prefersReducedMotion
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 0.3 }
      }
    : {
        initial: { opacity: 0, x: 40, scale: 0.98 },
        animate: { opacity: 1, x: 0, scale: 1 },
        exit: { opacity: 0, x: -40, scale: 0.98 },
        transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
      };

  return (
    <Card 
      className="relative overflow-hidden border-0 shadow-2xl" 
      data-testid="hero-carousel"
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${scene.gradient} transition-all duration-700`} />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-white/5 via-transparent to-transparent" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
      
      <div className="relative p-4 sm:p-8 md:p-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-4 sm:mb-8">
            <motion.div
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Badge 
                variant="outline" 
                className="mb-2 sm:mb-4 text-xs sm:text-sm bg-background/80 backdrop-blur-sm border-primary/20 shadow-lg"
              >
                <img src={recoverMeLogo} alt="ForgeRestore" className="w-4 h-4 mr-2 object-contain rounded-sm" />
                <span className="font-semibold">ForgeRestore</span>
              </Badge>
            </motion.div>
            <motion.h1
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3 bg-gradient-to-r from-foreground via-foreground to-foreground/70 bg-clip-text"
            >
              ForgeRestore: A Recovery App for Sponsors, Sponsees, and Step Work
            </motion.h1>
            <motion.p 
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-xs sm:text-base text-muted-foreground max-w-2xl mx-auto"
            >
              A recovery community for sponsors and sponsees working the 12 steps to connect, learn, and live.
            </motion.p>
          </div>

          <div className="relative min-h-[260px] sm:min-h-[380px] flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentScene}
                {...animationVariants}
                className="w-full"
              >
                <div className="grid md:grid-cols-2 gap-4 sm:gap-8 items-center">
                  <div className="flex justify-center">
                    <motion.div
                      initial={{ scale: 0.95, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.15, duration: 0.4, ease: "easeOut" }}
                      className="relative"
                    >
                      {/* Scene 0: Track Triggers & Cravings */}
                      {currentScene === 0 && (
                        <div className="space-y-3 w-72 sm:w-80" data-testid="scene-triggers">
                          {[
                            { label: "Trigger Logged", time: "2:30 PM", intensity: "High", gradient: "from-red-500 to-rose-600" },
                            { label: "Craving Tracked", time: "4:15 PM", intensity: "Medium", gradient: "from-orange-500 to-amber-600" },
                            { label: "Outcome Recorded", time: "4:45 PM", intensity: "Resolved", gradient: "from-green-500 to-emerald-600" },
                          ].map((item, i) => (
                            <motion.div
                              key={i}
                              initial={{ x: prefersReducedMotion ? 0 : -60, opacity: 0, scale: 0.9 }}
                              animate={{ x: 0, opacity: 1, scale: 1 }}
                              transition={{ delay: i * 0.1, duration: 0.4, ease: "easeOut" }}
                              className="flex items-center gap-3 p-4 bg-card/90 backdrop-blur-sm rounded-xl border border-border/50 shadow-lg hover:shadow-xl transition-shadow"
                              data-testid={`trigger-item-${i}`}
                            >
                              <div className={`p-2.5 rounded-lg bg-gradient-to-br ${item.gradient} shadow-md`}>
                                <AlertTriangle className="w-5 h-5 text-white" />
                              </div>
                              <div className="flex-1">
                                <p className="font-semibold text-sm">{item.label}</p>
                                <p className="text-xs text-muted-foreground">{item.time}</p>
                              </div>
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${item.intensity === 'Resolved' ? 'border-green-500/50 text-green-600' : item.intensity === 'High' ? 'border-red-500/50 text-red-600' : 'border-orange-500/50 text-orange-600'}`}
                              >
                                {item.intensity}
                              </Badge>
                            </motion.div>
                          ))}
                        </div>
                      )}

                      {/* Scene 1: Daily Recovery Journaling */}
                      {currentScene === 1 && (
                        <div className="space-y-4 w-72 sm:w-80" data-testid="scene-journaling">
                          <motion.div
                            initial={{ y: -30, opacity: 0, scale: 0.95 }}
                            animate={{ y: 0, opacity: 1, scale: 1 }}
                            transition={{ delay: 0.1, duration: 0.4, ease: "easeOut" }}
                            className="p-5 bg-card/90 backdrop-blur-sm rounded-xl border border-border/50 shadow-lg"
                          >
                            <div className="flex items-center gap-2 mb-3">
                              <div className="p-1.5 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600">
                                <PenTool className="w-3.5 h-3.5 text-white" />
                              </div>
                              <span className="text-xs font-medium text-muted-foreground">Today's Entry</span>
                              <motion.div
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{ repeat: Infinity, duration: 2 }}
                                className="ml-auto"
                              >
                                <Sparkles className="w-4 h-4 text-amber-500" />
                              </motion.div>
                            </div>
                            <div className="space-y-2">
                              <motion.div 
                                initial={{ width: 0 }}
                                animate={{ width: "100%" }}
                                transition={{ delay: 0.3, duration: 0.5 }}
                                className="h-2.5 bg-gradient-to-r from-primary/40 to-primary/20 rounded-full" 
                              />
                              <motion.div 
                                initial={{ width: 0 }}
                                animate={{ width: "85%" }}
                                transition={{ delay: 0.4, duration: 0.5 }}
                                className="h-2.5 bg-gradient-to-r from-primary/30 to-primary/10 rounded-full" 
                              />
                              <motion.div 
                                initial={{ width: 0 }}
                                animate={{ width: "70%" }}
                                transition={{ delay: 0.5, duration: 0.5 }}
                                className="h-2.5 bg-gradient-to-r from-primary/20 to-primary/5 rounded-full" 
                              />
                            </div>
                          </motion.div>
                          <div className="flex gap-2">
                            {["Mon", "Tue", "Wed", "Thu", "Fri"].map((day, i) => (
                              <motion.div
                                key={day}
                                initial={{ scale: 0, rotate: -10 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ delay: 0.2 + i * 0.06, duration: 0.35, ease: "easeOut" }}
                                className={`flex-1 p-2.5 rounded-lg text-center shadow-md ${
                                  i < 4 
                                    ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white' 
                                    : 'bg-card/80 text-muted-foreground border border-border/50'
                                }`}
                              >
                                <span className="text-xs font-bold">{day}</span>
                                {i < 4 && (
                                  <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.5 + i * 0.1 }}
                                  >
                                    <CheckCircle2 className="w-3.5 h-3.5 mx-auto mt-1" />
                                  </motion.div>
                                )}
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Scene 2: Sponsor-Sponsee Connection */}
                      {currentScene === 2 && (
                        <div className="flex items-center gap-6" data-testid="scene-sponsor-connection">
                          <motion.div 
                            initial={{ x: -50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.1, duration: 0.4, ease: "easeOut" }}
                            className="text-center"
                          >
                            <div className="relative">
                              <div className="absolute -inset-2 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full blur-md opacity-40" />
                              <Avatar className="relative w-20 h-20 sm:w-24 sm:h-24 border-4 border-white shadow-xl" data-testid="avatar-sponsor">
                                <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-green-600 text-white text-xl font-bold">
                                  SP
                                </AvatarFallback>
                              </Avatar>
                            </div>
                            <p className="text-sm font-semibold mt-3">Sponsor</p>
                          </motion.div>
                          
                          <motion.div
                            animate={{ x: prefersReducedMotion ? 0 : [0, 10, 0] }}
                            transition={{ repeat: Infinity, duration: 2 }}
                            data-testid="icon-connection"
                          >
                            <Users className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
                          </motion.div>
                          
                          <motion.div 
                            initial={{ x: 50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.2, duration: 0.4, ease: "easeOut" }}
                            className="text-center"
                          >
                            <div className="relative">
                              <div className="absolute -inset-2 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full blur-md opacity-40" />
                              <Avatar className="relative w-20 h-20 sm:w-24 sm:h-24 border-4 border-white shadow-xl" data-testid="avatar-sponsee">
                                <AvatarFallback className="bg-gradient-to-br from-teal-500 to-cyan-600 text-white text-xl font-bold">
                                  SE
                                </AvatarFallback>
                              </Avatar>
                            </div>
                            <p className="text-sm font-semibold mt-3">Sponsee</p>
                          </motion.div>
                        </div>
                      )}

                      {/* Scene 3: Integrated Stepwork Portal */}
                      {currentScene === 3 && (
                        <div className="space-y-3 w-72 sm:w-80" data-testid="scene-assignments">
                          {[
                            { icon: BookOpen, label: "Literature", step: "Read & Reflect", gradient: "from-blue-500 to-indigo-600" },
                            { icon: Target, label: "Writing", step: "View and submit assignments", gradient: "from-green-500 to-emerald-600" },
                            { icon: Users, label: "Discussion", step: "Share & Connect", gradient: "from-purple-500 to-violet-600" },
                          ].map((cat, i) => {
                            const CatIcon = cat.icon;
                            return (
                              <motion.div
                                key={i}
                                initial={{ x: -60, opacity: 0, scale: 0.9 }}
                                animate={{ x: 0, opacity: 1, scale: 1 }}
                                transition={{ delay: i * 0.08, duration: 0.4, ease: "easeOut" }}
                                className="flex items-center gap-3 p-4 bg-card/90 backdrop-blur-sm rounded-xl border border-border/50 shadow-lg"
                                data-testid={`assignment-category-${i}`}
                              >
                                <div className={`p-2.5 rounded-lg bg-gradient-to-br ${cat.gradient} shadow-md`}>
                                  <CatIcon className="w-5 h-5 text-white" />
                                </div>
                                <div className="flex-1">
                                  <p className="font-semibold text-sm">{cat.label}</p>
                                  <p className="text-xs text-muted-foreground">{cat.step}</p>
                                </div>
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ delay: 0.3 + i * 0.08, duration: 0.35, ease: "easeOut" }}
                                >
                                  <div className="p-1 rounded-full bg-green-500/20">
                                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                                  </div>
                                </motion.div>
                              </motion.div>
                            );
                          })}
                        </div>
                      )}

                      {/* Scene 4: Family Tree with Animated Lines */}
                      {currentScene === 4 && (
                        <div className="flex flex-col items-center scale-[0.85] sm:scale-100 origin-center" data-testid="scene-family-tree">
                          {/* Grand-Sponsor at top */}
                          <motion.div
                            initial={{ y: -20, opacity: 0, scale: 0.8 }}
                            animate={{ y: 0, opacity: 1, scale: 1 }}
                            transition={{ delay: 0.1, duration: 0.4, ease: "easeOut" }}
                            className="flex flex-col items-center"
                          >
                            <div className="relative">
                              <div className="absolute -inset-1.5 bg-gradient-to-br from-slate-400 to-gray-500 rounded-full blur-sm opacity-40" />
                              <Avatar className="relative w-11 h-11 border-2 border-white shadow-lg">
                                <AvatarFallback className="bg-gradient-to-br from-slate-400 to-gray-500 text-white font-bold text-[10px]">
                                  GS
                                </AvatarFallback>
                              </Avatar>
                            </div>
                            <span className="text-[10px] text-muted-foreground mt-0.5">Grand-Sponsor</span>
                          </motion.div>
                          
                          {/* Red line: Upward connection from Sponsor to Grand-Sponsor */}
                          <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: 20 }}
                            transition={{ delay: 0.3, duration: 0.4, ease: "easeOut" }}
                            className="w-1 bg-red-500 rounded-full my-0.5"
                          />
                          
                          {/* Sponsor */}
                          <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.35, duration: 0.4, ease: "easeOut" }}
                            className="flex flex-col items-center"
                          >
                            <div className="relative">
                              <div className="absolute -inset-1.5 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full blur-sm opacity-40" />
                              <Avatar className="relative w-12 h-12 border-2 border-white shadow-lg">
                                <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-green-600 text-white font-bold text-xs">
                                  SP
                                </AvatarFallback>
                              </Avatar>
                            </div>
                            <span className="text-[10px] text-muted-foreground mt-0.5">Sponsor</span>
                          </motion.div>
                          
                          {/* Red line: Upward connection from YOU to Sponsor */}
                          <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: 20 }}
                            transition={{ delay: 0.7, duration: 0.4, ease: "easeOut" }}
                            className="w-1 bg-red-500 rounded-full my-0.5"
                          />
                          
                          {/* Current User (YOU) */}
                          <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.55, duration: 0.4, ease: "easeOut" }}
                            className="flex flex-col items-center"
                          >
                            <div className="relative">
                              <div className="absolute -inset-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full blur-md opacity-50" />
                              <Avatar className="relative w-14 h-14 border-3 border-white shadow-xl ring-2 ring-primary">
                                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-sm font-bold">
                                  YOU
                                </AvatarFallback>
                              </Avatar>
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.7, duration: 0.35, ease: "easeOut" }}
                                className="absolute -bottom-0.5 -right-0.5 p-0.5 rounded-full bg-white shadow-md"
                              >
                                <Sparkles className="w-3 h-3 text-amber-500" />
                              </motion.div>
                            </div>
                            <span className="text-xs font-medium mt-0.5">You</span>
                          </motion.div>
                          
                          {/* Blue line: Downward connection from YOU to Sponsees */}
                          <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: 20 }}
                            transition={{ delay: 0.7, duration: 0.35, ease: "easeOut" }}
                            className="w-1 bg-blue-500 rounded-full my-0.5"
                          />
                          
                          {/* Horizontal bar and sponsees */}
                          <div className="relative">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: 64 }}
                              transition={{ delay: 0.8, duration: 0.35, ease: "easeOut" }}
                              className="absolute top-0 left-1/2 -translate-x-1/2 h-1 bg-blue-500 rounded-full"
                            />
                            <motion.div
                              initial={{ height: 0 }}
                              animate={{ height: 12 }}
                              transition={{ delay: 0.9, duration: 0.25, ease: "easeOut" }}
                              className="absolute top-0 left-[calc(50%-32px)] w-1 bg-blue-500 rounded-full"
                            />
                            <motion.div
                              initial={{ height: 0 }}
                              animate={{ height: 12 }}
                              transition={{ delay: 0.9, duration: 0.25, ease: "easeOut" }}
                              className="absolute top-0 left-[calc(50%+28px)] w-1 bg-blue-500 rounded-full"
                            />
                            
                            <motion.div
                              initial={{ y: 20, opacity: 0 }}
                              animate={{ y: 0, opacity: 1 }}
                              transition={{ delay: 0.95, duration: 0.35, ease: "easeOut" }}
                              className="flex gap-6 pt-4"
                            >
                              <div className="flex flex-col items-center">
                                <div className="relative">
                                  <div className="absolute -inset-1 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full blur-sm opacity-40" />
                                  <Avatar className="relative w-10 h-10 border-2 border-white shadow-lg">
                                    <AvatarFallback className="bg-gradient-to-br from-teal-500 to-cyan-600 text-white font-bold text-[10px]">
                                      S1
                                    </AvatarFallback>
                                  </Avatar>
                                </div>
                                <span className="text-[9px] text-muted-foreground mt-0.5">Sponsee</span>
                              </div>
                              <div className="flex flex-col items-center">
                                <div className="relative">
                                  <div className="absolute -inset-1 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full blur-sm opacity-40" />
                                  <Avatar className="relative w-10 h-10 border-2 border-white shadow-lg">
                                    <AvatarFallback className="bg-gradient-to-br from-teal-500 to-cyan-600 text-white font-bold text-[10px]">
                                      S2
                                    </AvatarFallback>
                                  </Avatar>
                                </div>
                                <span className="text-[9px] text-muted-foreground mt-0.5">Sponsee</span>
                              </div>
                            </motion.div>
                          </div>
                        </div>
                      )}

                      {/* Scene 6: Private by design */}
                      {currentScene === 6 && (
                        <div className="w-72 sm:w-80 flex items-center justify-center" data-testid="carousel-scene-privacy">
                          <motion.div
                            initial={prefersReducedMotion ? { opacity: 0 } : { scale: 0.85, opacity: 0, rotate: -6 }}
                            animate={prefersReducedMotion ? { opacity: 1 } : { scale: 1, opacity: 1, rotate: 0 }}
                            transition={{ delay: 0.15, duration: 0.5, ease: "easeOut" }}
                            className="relative"
                          >
                            <div className="absolute -inset-6 bg-gradient-to-br from-indigo-500/40 to-cyan-500/40 rounded-full blur-2xl opacity-60" />
                            <div className="relative w-44 h-44 sm:w-52 sm:h-52 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 shadow-2xl flex items-center justify-center"
                              style={{ boxShadow: '0 10px 40px rgba(79, 70, 229, 0.45), inset 0 4px 10px rgba(255,255,255,0.25)' }}
                            >
                              <div className="absolute inset-3 rounded-full border-2 border-dashed border-white/40" />
                              <div className="absolute inset-7 rounded-full border-2 border-white/30" />
                              <ShieldCheck className="w-20 h-20 sm:w-24 sm:h-24 text-white drop-shadow-lg" />
                            </div>
                          </motion.div>
                        </div>
                      )}

                      {/* Scene 5: Milestone Celebrations */}
                      {currentScene === 5 && (
                        <div className="grid grid-cols-4 gap-5 sm:gap-6" data-testid="scene-milestones">
                          {[
                            { bg: "bg-red-600", border: "border-red-400", accent: "bg-red-500", text: "30", label: "DAYS" },
                            { bg: "bg-orange-500", border: "border-orange-300", accent: "bg-orange-400", text: "60", label: "DAYS" },
                            { bg: "bg-emerald-600", border: "border-emerald-400", accent: "bg-emerald-500", text: "90", label: "DAYS" },
                            { bg: "bg-violet-600", border: "border-violet-400", accent: "bg-violet-500", text: "6 MO", label: "" },
                            { bg: "bg-blue-600", border: "border-blue-400", accent: "bg-blue-500", text: "1 YR", label: "" },
                            { bg: "bg-pink-500", border: "border-pink-300", accent: "bg-pink-400", text: "18 MO", label: "" },
                            { bg: "bg-amber-500", border: "border-amber-300", accent: "bg-amber-400", text: "2 YR", label: "" },
                            { bg: "bg-cyan-600", border: "border-cyan-400", accent: "bg-cyan-500", text: "5 YR", label: "" },
                          ].map((chip, i) => (
                            <motion.div
                              key={i}
                              initial={prefersReducedMotion ? { opacity: 0 } : { scale: 0, rotate: -180, opacity: 0 }}
                              animate={prefersReducedMotion ? { opacity: 1 } : { scale: 1, rotate: 0, opacity: 1 }}
                              transition={{ delay: i * 0.06, duration: 0.4, ease: "easeOut" }}
                              className="text-center"
                              data-testid={`milestone-chip-${i}`}
                            >
                              <motion.div
                                whileHover={{ scale: 1.1, rotate: 10 }}
                                className={`relative w-20 h-20 sm:w-24 sm:h-24 rounded-full mx-auto ${chip.bg} shadow-xl`}
                                style={{ boxShadow: '0 6px 20px rgba(0,0,0,0.35), inset 0 3px 6px rgba(255,255,255,0.25)' }}
                              >
                                <div className={`absolute inset-[4px] rounded-full border-2 border-dashed ${chip.border} opacity-60`} />
                                <div className={`absolute inset-[10px] rounded-full border-2 ${chip.border}`} />
                                <div className={`absolute inset-[16px] rounded-full ${chip.accent} flex flex-col items-center justify-center shadow-inner`}>
                                  <span className="text-white font-black text-sm sm:text-base leading-tight drop-shadow-sm">
                                    {chip.text}
                                  </span>
                                  {chip.label && (
                                    <span className="text-white/90 text-[7px] sm:text-[8px] font-bold tracking-wider">
                                      {chip.label}
                                    </span>
                                  )}
                                </div>
                                {[...Array(12)].map((_, j) => (
                                  <div
                                    key={j}
                                    className={`absolute w-[4px] h-[8px] ${chip.border} rounded-sm`}
                                    style={{
                                      top: '50%',
                                      left: '50%',
                                      transform: `rotate(${j * 30}deg) translateY(-38px) translateX(-2px)`,
                                      opacity: 0.8
                                    }}
                                  />
                                ))}
                              </motion.div>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  </div>

                  <div className="text-center md:text-left">
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.3 }}
                      className="inline-flex p-4 bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl mb-4 shadow-lg backdrop-blur-sm border border-primary/10"
                    >
                      <Icon className="w-10 h-10 sm:w-12 sm:h-12 text-primary" />
                    </motion.div>
                    <motion.h3
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      className="text-xl sm:text-2xl md:text-3xl font-bold mb-3"
                    >
                      {scene.title}
                    </motion.h3>
                    <motion.p
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.5 }}
                      className="text-sm sm:text-base text-muted-foreground leading-relaxed"
                    >
                      {scene.description}
                    </motion.p>
                    {scene.bullets && (
                      <motion.ul
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.6 }}
                        className="mt-4 space-y-1.5 text-sm text-muted-foreground list-disc list-inside text-left mx-auto md:mx-0 max-w-md"
                        data-testid="carousel-scene-privacy-bullets"
                      >
                        {scene.bullets.map((b, i) => (
                          <li key={i}>{b}</li>
                        ))}
                      </motion.ul>
                    )}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex items-center justify-center gap-4 mt-8">
            <Button
              variant="outline"
              size="icon"
              onClick={prevScene}
              className="bg-background/80 backdrop-blur-sm shadow-md hover:shadow-lg transition-shadow"
              data-testid="button-prev-scene"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>

            <div className="flex gap-2 items-center bg-background/60 backdrop-blur-sm rounded-full px-4 py-2">
              {scenes.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentScene(index)}
                  className={`transition-all rounded-full ${
                    index === currentScene
                      ? "bg-primary w-8 h-2.5 shadow-md"
                      : "bg-muted-foreground/30 hover:bg-muted-foreground/50 w-2.5 h-2.5"
                  }`}
                  data-testid={`dot-scene-${index}`}
                  aria-label={`Go to scene ${index + 1}`}
                />
              ))}
            </div>

            <Button
              variant="outline"
              size="icon"
              onClick={() => setIsPaused(!isPaused)}
              className="bg-background/80 backdrop-blur-sm shadow-md hover:shadow-lg transition-shadow"
              data-testid="button-play-pause"
            >
              {isPaused ? (
                <Play className="w-4 h-4" />
              ) : (
                <Pause className="w-4 h-4" />
              )}
            </Button>

            <Button
              variant="outline"
              size="icon"
              onClick={nextScene}
              className="bg-background/80 backdrop-blur-sm shadow-md hover:shadow-lg transition-shadow"
              data-testid="button-next-scene"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
